/**
 * THE PORTAL'S HOST API — what the portal hands the help widget (plan-019 WAPP-HOST; design §31.9.2; contract IC-H10,
 * `@wetalk/help-widget`'s `HelpHostApi`).
 *
 * The widget never touches the page: every page effect goes through these functions.
 *
 * - `canOpen(routeId)` asks ADR 0033's evaluator over the shell's capability state (`./route-access.ts`), so a Viewer is
 *   never offered a screen they cannot open (FR-HA-17).
 * - `navigate(routeId)` is called only when the person pressed the proposal's button (OD-4: never automatic); it checks
 *   `canOpen` again and opens the registered path in the current workspace or organization through TanStack Router.
 * - `fillable`, `fill`, `undo` are thin wrappers over the fill registry (`./fill/fill-registry.ts`): the forms' own
 *   setters, per field, no request (OD-23), no focus, undo in memory.
 * - `pageContext()` is OD-5's: the page (the page registry's id — every route of the portal has one, the new-agent
 *   wizard and an agent's page included — else the route's own path as a slug, never a record id), the screen label,
 *   whether a workspace is open, the mounted registered forms with their current values, and — from the place registry
 *   (`./place/**`, owner request 2026-10-17) — the step or tab shown, the ones offered and which fields are empty.
 * - `onPageChange` fires on every resolved navigation and every place change, so the widget re-sends page context.
 * - A move within the page (a wizard step, a tab: `helpPlaceRouteOf`) opens only on the page it belongs to, through the
 *   screen's own `move`, and only to a step or tab the screen offers now.
 * - `onScopeChange` fires on a workspace or organization switch, a sign-in or sign-out, and once a `409 scope_changed`
 *   has been read again; the widget then asks a new grant before the next message (FR-HA-5).
 * - `grant()`, `unreadCount()` and `helpApi()` (the widget's request form and "Your messages", WID-FEAT) are the only
 *   requests, through `./help-gateway.ts` and the console gateway's `help` slice.
 *
 * - AMENDED 2026-10-18 (ADR 0072): `pageReading()` / `setPageReading()` read and record the signed-in person's choice
 *   through the console gateway (cached until a scope change), and `pageSnapshot()` reads the visible page
 *   (`./snapshot/page-snapshot.ts`) — ONLY while that choice is `allowed`; otherwise, and signed out, `null`.
 *
 * Built once per root by `./help-root.tsx` over getters of the latest page state, so the object the widget holds never
 * goes stale.
 */
import {
  HELP_ROUTE_REGISTRY,
  helpPageIdOfLocation,
  helpPlaceRouteOf,
  isHelpLanguage,
  type HelpFormId,
  type HelpGrantAnswer,
  type HelpLanguage,
  type HelpPageReading,
  type HelpPageSnapshot,
  type HelpRouteId,
} from "@wetalk/domain";
import type { HelpHostApi, HelpPageContext } from "@wetalk/help-widget";

import type { AuthSessionView, ConsoleGateway } from "../api/gateway.js";
import type { HelpFillRegistry } from "./fill/fill-registry.js";
import type { HelpPlaceRegistry } from "./place/place-registry.js";
import { helpGrantOf, helpUnreadCountOf, type AnonymousHelpEnvironment, type HelpPlace } from "./help-gateway.js";
import { canOpenHelpRoute } from "./route-access.js";
import { pageSnapshotOf, snapshotRootOf } from "@wetalk/help-widget/snapshot";

/** The route ids the help records accept (0153's CHECK, the protocol's `route_id`). */
const ROUTE_ID = /^[a-z][a-z0-9_.]{0,79}$/;
/** The grant's `screen_label` ceiling. */
const SCREEN_LABEL_CHAR_LIMIT = 120;

/** What the host reads of the page, each time it is asked. */
export interface PortalHelpPage {
  /** The auth_session the shell reads (`authContextQuery`); `null` signed out, `undefined` while unknown. */
  readonly authSession: () => AuthSessionView | null | undefined;
  /** The portal's display language (ADR 0051). */
  readonly language: () => string;
  /** The router's current pathname and the matched leaf route's full path (`/agents/$voiceAgentId`). */
  readonly location: () => { readonly pathname: string; readonly routePath: string };
  /** The mounted screen's `data-screen-label`, or `null`. */
  readonly screenLabel: () => string | null;
  /** Opens a path in the current scope (TanStack Router). */
  readonly navigateTo: (path: string) => void;
  /** Calls `listener` after every resolved navigation; returns the unsubscribe. */
  readonly onLocation: (listener: () => void) => () => void;
  readonly now: () => Date;
  /** ADR 0072: the document the page snapshot is read from (`null`: none, as in a suite without a DOM). */
  readonly document: () => Document | null;
}

export interface PortalHelpHostSettings {
  /** The portal's build tag (`VITE_WETALK_CONSOLE_VERSION`). */
  readonly build: string;
  readonly gateway: ConsoleGateway;
  readonly registry: HelpFillRegistry;
  /** The step or tab the screen shows (`useHelpPlace`). */
  readonly places: HelpPlaceRegistry;
  readonly page: PortalHelpPage;
  readonly anonymous: AnonymousHelpEnvironment;
  /** The widget cannot be seen or reached: the person menu's Help item opens `/help` instead. */
  readonly onUnavailable: () => void;
  /**
   * Whether help takes a new conversation now: the help status through the console's query cache (one read for the
   * mount, the person menu and the widget; owner decision 2026-10-15).
   */
  readonly available: () => Promise<boolean>;
  /**
   * plan-025 A-WAPP (design §36.10): a confirmed card's request was answered — the screens read again, so the person
   * sees the change (or that nothing changed) where they are.
   */
  readonly afterAccountAction: () => void;
}

/** The host API plus the two signals the mount drives. */
export interface PortalHelpHost extends HelpHostApi {
  /** The display language changed (the portal's language picker). */
  languageChanged(): void;
  /** The scope changed (a switch, a sign-in or sign-out, a re-read after `409 scope_changed`). */
  scopeChanged(): void;
}

/**
 * The page's id: the page registry's (`helpPageIdOfLocation`: a registered screen by its pathname, any other page by
 * its route), otherwise the matched route's own path as a dotted slug — parameter names only, never a value — or
 * `null` when nothing fits the record's pattern.
 */
export function helpPageRouteIdOf(pathname: string, routePath: string): string | null {
  const registered = helpPageIdOfLocation(pathname, routePath);
  if (registered !== null) return registered;
  const slug = routePath
    .toLowerCase()
    .replace(/[$]/g, "")
    .split(/[/-]+/)
    .filter((segment) => segment !== "")
    .join(".")
    .replace(/[^a-z0-9_.]/g, "");
  return ROUTE_ID.test(slug) ? slug : null;
}

function clippedLabel(label: string | null): string | null {
  const text = label === null ? "" : label.trim().slice(0, SCREEN_LABEL_CHAR_LIMIT);
  return text === "" ? null : text;
}

export function createPortalHelpHost(settings: PortalHelpHostSettings): PortalHelpHost {
  const { gateway, registry, page } = settings;
  const languageListener = new Set<(language: HelpLanguage) => void>();
  const scopeListener = new Set<() => void>();
  /** ADR 0072: the person's saved choice as last read or written here; forgotten on a scope change. */
  let reading: HelpPageReading | null = null;
  /** ADR 0076: whether that choice also lets a wetalk person see the page (the newer wording). */
  let operatorSees = false;
  /** OD-3 (owner, 2026-10-21): allowed for the assistant only and not yet asked again. */
  let asksAgain = false;

  const signedIn = (): boolean => {
    const current = page.authSession();
    return current !== null && current !== undefined;
  };

  const place = (): HelpPlace => {
    const { pathname, routePath } = page.location();
    return { routeId: helpPageRouteIdOf(pathname, routePath), screenLabel: clippedLabel(page.screenLabel()) };
  };

  /** A move within a page: only on that page, only to a step or tab the screen offers now. */
  const placeOpen = (routeId: HelpRouteId): boolean => {
    const move = helpPlaceRouteOf(routeId);
    if (move === null) return true;
    const entry = settings.places.current();
    return place().routeId === move.within && entry !== null && entry.placeList.includes(move.place);
  };

  const canOpen = (routeId: HelpRouteId): boolean => {
    if (!Object.hasOwn(HELP_ROUTE_REGISTRY, routeId)) return false;
    const current = page.authSession();
    return current !== undefined && canOpenHelpRoute(current, routeId, page.now()) && placeOpen(routeId);
  };

  function snapshotNow(): HelpPageSnapshot | null {
    // Only while the person's saved choice, as this page last read or wrote it, is `allowed` — the help host checks
    // the stored choice again before it reads a word of it.
    if (!signedIn() || reading !== "allowed") return null;
    const doc = page.document();
    if (doc === null) return null;
    const forms = registry.fillable();
    return pageSnapshotOf(snapshotRootOf(doc), {
      formOfField: (code) => forms.find((form) => form.field.some((field) => field.code === code))?.formId ?? null,
    });
  }

  return {
    surface: "portal",
    build: settings.build,

    language() {
      return page.language() as HelpLanguage;
    },

    onLanguage(listener) {
      languageListener.add(listener);
      return () => {
        languageListener.delete(listener);
      };
    },

    signedIn,

    grant(): Promise<HelpGrantAnswer> {
      const language = page.language();
      if (!isHelpLanguage(language)) return Promise.reject(new Error(`The help assistant does not speak ${language}.`));
      if (page.authSession() === undefined) return Promise.reject(new Error("The sign-in is not known yet."));
      return helpGrantOf(gateway, signedIn(), language, place(), settings.anonymous);
    },

    canOpen,

    navigate(routeId) {
      if (!canOpen(routeId)) return;
      const move = helpPlaceRouteOf(routeId);
      if (move !== null) {
        settings.places.move(move.place);
        return;
      }
      page.navigateTo(HELP_ROUTE_REGISTRY[routeId].path);
    },

    linkOf: () => null,

    fillable: () => registry.fillable(),

    onFillableChange: (listener) => registry.subscribe(listener),

    fill: (formId: HelpFormId, proposalId, field) => registry.fill(formId, proposalId, field),

    undo: (proposalId) => registry.undo(proposalId),

    pageContext(): HelpPageContext {
      const current = page.authSession();
      const here = place();
      const entry = settings.places.current();
      return {
        ...(here.routeId === null ? {} : { routeId: here.routeId }),
        ...(here.screenLabel === null ? {} : { screenLabel: here.screenLabel }),
        workspaceOpen: current !== null && current !== undefined && current.kind === "workspace_selected",
        form: registry.fillable(),
        ...(entry === null ? {} : { place: entry.place, placeList: entry.placeList, state: entry.state }),
      };
    },

    onPageChange(listener) {
      const stopLocation = page.onLocation(listener);
      const stopPlace = settings.places.subscribe(listener);
      return () => {
        stopLocation();
        stopPlace();
      };
    },

    onScopeChange(listener) {
      scopeListener.add(listener);
      return () => {
        scopeListener.delete(listener);
      };
    },

    unreadCount() {
      return signedIn() ? helpUnreadCountOf(gateway) : Promise.resolve(0);
    },

    // WID-FEAT's request form and "Your messages": carried by the portal's api client (same origin, the cookie, the
    // CSRF token and the scope generation), whatever the status.
    helpApi: (request) => gateway.carryHelpApiRequest(request),

    // plan-025 A-WAPP (design §36.10): a confirmed confirm card, sent as the person's own request — only signed in, only
    // a registry request (`./account-action-client.ts` checks the operation, method and path against the registry).
    async act(request, option) {
      if (!signedIn()) return { status: 401, code: "authentication_required" };
      try {
        return await gateway.carryHelpAccountAction(request, option.helpActionId);
      } finally {
        settings.afterAccountAction();
      }
    },

    onUnavailable: settings.onUnavailable,

    available: settings.available,

    async pageReading() {
      if (!signedIn()) return null;
      const read = await gateway.readHelpPageReading();
      reading = read.choice;
      operatorSees = read.operatorSees;
      asksAgain = read.askAgain;
      return reading;
    },

    pageReadingAsksAgain: () => asksAgain,

    async keepPageReadingForAssistant() {
      const kept = await gateway.keepHelpPageReadingForAssistant();
      reading = kept.choice;
      operatorSees = kept.operatorSees;
      asksAgain = kept.askAgain;
    },

    async setPageReading(choice, consentRevision) {
      const written = await gateway.setHelpPageReading(choice, consentRevision);
      reading = written.choice;
      operatorSees = written.operatorSees;
      asksAgain = written.askAgain;
      return reading;
    },

    pageSnapshot: snapshotNow,

    pageSnapshotForPerson(): HelpPageSnapshot | null {
      // ADR 0076: only while the answer this page last read or wrote names a wetalk person too — the api and the help
      // host read the saved answer again before an operator sees a word of it.
      return operatorSees ? snapshotNow() : null;
    },

    languageChanged() {
      const language = page.language();
      for (const one of [...languageListener]) one(language as HelpLanguage);
    },

    scopeChanged() {
      reading = null;
      operatorSees = false;
      asksAgain = false;
      for (const one of [...scopeListener]) one();
    },
  };
}
